/**
 * TreeDataProviderError
 *
 * Custom error class for tree data provider errors
 * Provides structured error information with standardized error codes
 */

/**
 * Standard error codes for provider operations
 */
export enum TreeDataProviderErrorCode {
  /** Authentication failed */
  AUTH_FAILED = 'AUTH_FAILED',

  /** Resource not found */
  NOT_FOUND = 'NOT_FOUND',

  /** Insufficient permissions */
  PERMISSION_DENIED = 'PERMISSION_DENIED',

  /** Network connection issue */
  NETWORK_ERROR = 'NETWORK_ERROR',

  /** Invalid node name */
  INVALID_NAME = 'INVALID_NAME',

  /** Node already exists */
  ALREADY_EXISTS = 'ALREADY_EXISTS',

  /** Parent node is invalid */
  INVALID_PARENT = 'INVALID_PARENT',

  /** Folder not empty (for delete) */
  NOT_EMPTY = 'NOT_EMPTY',

  /** Invalid operation */
  INVALID_OPERATION = 'INVALID_OPERATION',

  /** Unknown error */
  UNKNOWN = 'UNKNOWN'
}

/**
 * Custom error class for tree data provider operations
 */
export class TreeDataProviderError extends Error {
  /**
   * Error code for programmatic handling
   */
  public code: TreeDataProviderErrorCode;

  /**
   * HTTP status code (if applicable)
   */
  public statusCode?: number;

  /**
   * Original error that caused this error (if applicable)
   */
  public originalError?: Error;

  /**
   * Create a new TreeDataProviderError
   * @param message - Human-readable error message
   * @param code - Error code
   * @param statusCode - HTTP status code (optional)
   * @param originalError - Original error (optional)
   */
  constructor(
    message: string,
    code: TreeDataProviderErrorCode,
    statusCode?: number,
    originalError?: Error
  ) {
    super(message);
    this.name = 'TreeDataProviderError';
    this.code = code;
    this.statusCode = statusCode;
    this.originalError = originalError;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    const errorConstructor = Error as ErrorConstructor & {
      captureStackTrace?: (target: Error, constructor: typeof TreeDataProviderError) => void;
    };
    if (typeof errorConstructor.captureStackTrace === 'function') {
      errorConstructor.captureStackTrace(this, TreeDataProviderError);
    }
  }

  /**
   * Convert error to JSON for logging/serialization
   */
  toJSON(): object {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      stack: this.stack,
      originalError: this.originalError ? {
        name: this.originalError.name,
        message: this.originalError.message,
        stack: this.originalError.stack
      } : undefined
    };
  }

  /**
   * Get a user-friendly error message
   * Provides more context than the raw error message
   */
  getUserMessage(): string {
    switch (this.code) {
      case TreeDataProviderErrorCode.AUTH_FAILED:
        return 'Authentication failed. Please log in and try again.';
      case TreeDataProviderErrorCode.NOT_FOUND:
        return 'The requested file or folder was not found.';
      case TreeDataProviderErrorCode.PERMISSION_DENIED:
        return 'You do not have permission to perform this action.';
      case TreeDataProviderErrorCode.NETWORK_ERROR:
        return 'Network error. Please check your connection and try again.';
      case TreeDataProviderErrorCode.INVALID_NAME:
        return `Invalid name: ${this.message}`;
      case TreeDataProviderErrorCode.ALREADY_EXISTS:
        return 'A file or folder with this name already exists.';
      case TreeDataProviderErrorCode.INVALID_PARENT:
        return 'Invalid parent folder.';
      case TreeDataProviderErrorCode.NOT_EMPTY:
        return 'Folder is not empty and cannot be deleted.';
      case TreeDataProviderErrorCode.INVALID_OPERATION:
        return `Invalid operation: ${this.message}`;
      default:
        return this.message || 'An unknown error occurred.';
    }
  }
}
